'use client'

import { AttentionTaskResponse } from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { useTestResponse } from '@/hooks/useTestResponse'
import { cn } from '@/utils/common'
import { useCallback, useEffect, useState } from 'react'

import LoadingCard from './components/LoadingCard'
import TaskCard from './components/TaskCard'

interface AttentionTaskProps {
  data: AttentionTaskResponse
  moduleId: string
  questionId: string
  onComplete?: () => void
  onBack?: () => void
  onStart?: () => void
  className?: string
}

interface TaskResults {
  correctSelections: number
  incorrectSelections: number
  missedTargets: number
  totalTargets: number
  totalSymbols: number
  accuracy: number
  timeSpent: number
  selectedTargets: {
    rowIndex: number
    symbolIndex: number
  }[]
}

interface Symbol {
  id: string
  value: string
  isTarget: boolean
  isSelected: boolean
}

enum TaskStage {
  TASK = 1,
  RESULT = 2
}

function AttentionTask({
  data,
  moduleId,
  questionId,
  onComplete,
  onStart,
  className
}: AttentionTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [stage, setStage] = useState<TaskStage>(TaskStage.TASK)
  const [rows, setRows] = useState<Symbol[][]>([])
  const [currentRowIndex, setCurrentRowIndex] = useState<number>(0)
  const [remainingTime, setRemainingTime] = useState<number>(0)
  const [startTime, setStartTime] = useState<number>(0)
  const [selectedTargets, setSelectedTargets] = useState<
    { rowIndex: number; symbolIndex: number }[]
  >([])
  const [_, setResults] = useState<TaskResults>({
    correctSelections: 0,
    incorrectSelections: 0,
    missedTargets: 0,
    totalTargets: 0,
    totalSymbols: 0,
    accuracy: 0,
    timeSpent: 0,
    selectedTargets: []
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)

  useEffect(()=>{startTask()},[])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const generateRows = useCallback(() => {
    try {
      const newRows: Symbol[][] = []
      let totalTargets = 0

      data.symbols.forEach((row) => {
        const newRow: Symbol[] = row.symbols.map((symbol) => {
          const value = symbol.letter + "'".repeat(symbol.dashCount)
          if (symbol.isTarget) totalTargets++

          return {
            id: `${row.rowIndex}-${symbol.index}`,
            value,
            isTarget: symbol.isTarget,
            isSelected: false
          }
        })
        newRows.push(newRow)
      })

      setRows(newRows)
      setSelectedTargets([])

      const totalSymbols = data.rowCount * data.symbolsPerRow

      setResults({
        correctSelections: 0,
        incorrectSelections: 0,
        missedTargets: 0,
        totalTargets,
        totalSymbols,
        accuracy: 0,
        timeSpent: 0,
        selectedTargets: []
      })

      return true
    } catch (error) {
      console.error('Błąd podczas generowania wierszy:', error)
      return false
    }
  }, [data])

  const startTask = useCallback(() => {
    setLoading(true)
    setTimeout(() => {
      const success = generateRows()
      if (success) {
        setCurrentRowIndex(0)
        setRemainingTime(data.timePerRowSeconds)
        setStartTime(Date.now())
        setStage(TaskStage.TASK)
        setSelectedTargets([])
        setDisabled(false)
        onStart?.()
      }
      setLoading(false)
    }, 500)
  }, [generateRows, data.timePerRowSeconds, onStart])

  const toggleSymbol = (rowIndex: number, symbolIndex: number) => {
    if (rowIndex !== currentRowIndex || disabled) return

    setRows((prevRows) => {
      const newRows = [...prevRows]
      const newRow = [...newRows[rowIndex]]
      const symbol = { ...newRow[symbolIndex] }

      symbol.isSelected = !symbol.isSelected
      newRow[symbolIndex] = symbol
      newRows[rowIndex] = newRow

      // Aktualizuj listę wybranych celów
      setSelectedTargets((prev) => {
        if (symbol.isSelected) {
          return [...prev, { rowIndex, symbolIndex }]
        } else {
          return prev.filter(
            (target) =>
              !(target.rowIndex === rowIndex && target.symbolIndex === symbolIndex)
          )
        }
      })

      return newRows
    })
  }

  const nextRow = useCallback(() => {
    if (disabled) return
    if (!rows[currentRowIndex]) return

    setDisabled(true)

    const currentRow = rows[currentRowIndex]
    let correctSelections = 0
    let incorrectSelections = 0
    let missedTargets = 0

    currentRow.forEach((symbol) => {
      if (symbol.isTarget && symbol.isSelected) correctSelections++
      if (!symbol.isTarget && symbol.isSelected) incorrectSelections++
      if (symbol.isTarget && !symbol.isSelected) missedTargets++
    })

    setResults((prev) => {
      const newResults = {
        ...prev,
        correctSelections: prev.correctSelections + correctSelections,
        incorrectSelections: prev.incorrectSelections + incorrectSelections,
        missedTargets: prev.missedTargets + missedTargets,
        selectedTargets: selectedTargets
      }
      return newResults
    })

    if (currentRowIndex < data.rowCount - 1) {
      setCurrentRowIndex((prev) => prev + 1)
      setRemainingTime(data.timePerRowSeconds)
      setDisabled(false)
    } else {
      setLoading(true)
      const timeSpent = (Date.now() - startTime) / 1000

      setResults((prev) => {
        const updatedResults = {
          ...prev,
          timeSpent,
          selectedTargets
        }

        addQuestionResponse(moduleId, {
          questionId,
          questionType: 'ATTENTION_TASK',
          attentionTaskAnswer: {
            selectedTargets: selectedTargets
          }
        })

        const accuracy =
          updatedResults.totalTargets > 0
            ? (updatedResults.correctSelections / updatedResults.totalTargets) * 100
            : 0

        return {
          ...updatedResults,
          accuracy
        }
      })

      setStage(TaskStage.RESULT)
      setLoading(false)
      if (onComplete) {
        onComplete()
      }
    }
  }, [
    currentRowIndex,
    rows,
    data.rowCount,
    data.timePerRowSeconds,
    startTime,
    onComplete,
    disabled,
    selectedTargets,
    moduleId,
    questionId,
    addQuestionResponse
  ])

  useEffect(() => {
    if (stage !== TaskStage.TASK) return

    if (remainingTime <= 0) {
      setDisabled(true)
      setTimeout(() => {
        nextRow()
      }, 1000)
      return
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [stage, remainingTime, nextRow])

  const renderContent = () => {
   

    return (
      <div className={cn('flex flex-col relative', className)}>
        {stage === TaskStage.TASK && !loading && (
          <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
            Pozostały czas: {formatTime(remainingTime)}
          </div>
        )}
        <main className="flex flex-1 flex-col items-center justify-center p-4">
          <Card
            className={cn(
              'w-full max-w-3xl min-w-[40vw] shadow-sm',
              stage === TaskStage.TASK && disabled && 'pointer-events-none opacity-60'
            )}
          >
            <CardContent className="p-6">
              {stage === TaskStage.TASK && rows.length > 0 && !loading && (
                <TaskCard
                  currentRowIndex={currentRowIndex}
                  disabled={disabled}
                  key={currentRowIndex}
                  onNextRow={nextRow}
                  onToggleSymbol={toggleSymbol}
                  rowCount={data.rowCount}
                  rows={rows}
                />
              )}

              {loading && <LoadingCard />}

              {/* {stage === TaskStage.RESULT && !loading && (
                <ResultsCard
                  loading={loading}
                  onBack={onBack}
                  onTryAgain={startTask}
                  results={results}
                />
              )} */}
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return renderContent()
}

export default AttentionTask
